import React, { useEffect, useState } from 'react';
import { IoMdArrowRoundForward } from "react-icons/io";
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaTimes } from 'react-icons/fa';
import { createAnOrder } from '../Api/order';
import { validateCoupon } from '../Api/coupon';
import { getGSTByCartAndPincode } from '../Api/gst';
import { getPincodeByNumber } from '../Api/location';
import '../assets/css/checkout.css';
import { getCustomerById, updateWooCustomer } from '../Api/customer';

const RAZORPAY_CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

// Module scope on purpose: one in-flight load per page session, shared by every
// render and every mount of Checkout.
//
// This script used to be a <script> tag in public/index.html, so it loaded on
// every route — home, product pages, the admin dashboard — and ran its init and
// tracking traffic for visitors who would never reach checkout. The in-component
// fallback that replaced it had its own bug: it appended the <script> and then
// called `new window.Razorpay(...)` on the very next line, without waiting for
// the load, so the constructor ran against an undefined global.
//
// Caching the promise is what stops repeat work: without it, each click would
// append another copy of the script, and every copy initialises and beacons
// independently.
let razorpayLoader = null;

const loadRazorpayCheckout = () => {
    if (typeof window === 'undefined') {
        return Promise.reject(new Error('Payment gateway unavailable.'));
    }
    if (window.Razorpay) {
        return Promise.resolve(window.Razorpay);
    }
    if (razorpayLoader) {
        return razorpayLoader;
    }

    razorpayLoader = new Promise((resolve, reject) => {
        const settle = () => {
            if (window.Razorpay) {
                resolve(window.Razorpay);
            } else {
                razorpayLoader = null;
                reject(new Error('Payment gateway loaded but did not initialise.'));
            }
        };
        const fail = () => {
            // Clear the cache so a genuine retry is possible — a cached rejected
            // promise would make the first network blip permanent for the session.
            razorpayLoader = null;
            reject(new Error('Could not reach the payment gateway. Check your connection and try again.'));
        };

        // Reuse a tag already in the document rather than adding a second one.
        const existing = document.querySelector(`script[src="${RAZORPAY_CHECKOUT_SRC}"]`);
        if (existing) {
            existing.addEventListener('load', settle, { once: true });
            existing.addEventListener('error', fail, { once: true });
            return;
        }

        const script = document.createElement('script');
        script.src = RAZORPAY_CHECKOUT_SRC;
        script.async = true;
        script.addEventListener('load', settle, { once: true });
        script.addEventListener('error', fail, { once: true });
        document.body.appendChild(script);
    });

    return razorpayLoader;
};

function Checkout({ onRemoveProduct, clearCartItems, isAuthenticated }) {
    const navigate = useNavigate();
    const { state } = useLocation();
    const customerNote = state?.customer_note || '';
    const [userData] = useState(JSON.parse(localStorage.getItem("user_data")));
    const [cartItems] = useState(JSON.parse(localStorage.getItem('cart')) || []);
    const [shippingCharge, setShippingCharge] = useState(0);
    const [selectedCoupon, setSelectedCoupon] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('cod');
    const [couponCode, setCouponCode] = useState('');
    const [couponError, setCouponError] = useState('');
    const [couponChecking, setCouponChecking] = useState(false);
    const [gst, setGst] = useState(0);
    const [gstBreakup, setGstBreakup] = useState({ cgst: 0, sgst: 0 });

    // MODAL STATES
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    // True from the moment the Razorpay modal is requested until it completes,
    // fails or is dismissed. Blocks a second checkout being opened.
    const [paymentInProgress, setPaymentInProgress] = useState(false);
    const [token] = useState(localStorage.getItem('auth_token'));

    const [billingInfo, setBillingInfo] = useState({
        first_name: '',
        last_name: '',
        address_1: '',
        address_2: '',
        city: '',
        postcode: '',
        state: '',
        country: '',
        phone: '',
        email: ''
    });

    const pincode =
        billingInfo?.postcode ||
        localStorage.getItem("pincode") ||
        "";

    useEffect(() => {
        if (!isAuthenticated) navigate('/');
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        async function fetchShipping() {
            if (!pincode) return; // ⬅️ DO NOT reset to 0

            try {
                const data = await getPincodeByNumber(pincode);

                if (data?.charge != null) {
                    setShippingCharge(Number(data.charge));
                }
            } catch (error) {
                console.error("Shipping fetch error:", error);
            }
        }

        fetchShipping();
    }, [pincode]);

    useEffect(() => {
        if (!cartItems.length || !pincode) {
            setGst(0);
            return;
        }

        let cancelled = false;

        async function fetchGST() {
            try {
                const data = await getGSTByCartAndPincode({
                    cartItems,
                    pincode
                });

                if (!cancelled) {
                    setGst(Number(data?.tax?.total ?? 0));
                    setGstBreakup({
                        cgst: Number(data?.tax?.breakup?.CGST ?? 0),
                        sgst: Number(data?.tax?.breakup?.SGST ?? 0),
                    });
                }
            } catch {
                if (!cancelled) setGst(0);
            }
        }

        fetchGST();
        return () => { cancelled = true };
    }, [cartItems, pincode]);

    // Fetch initial customer data
    useEffect(() => {
        async function fetchCustomerAddress() {
            try {
                if (!token || !userData?.id) return;
                const customer = await getCustomerById(userData.id, token);

                setBillingInfo({
                    first_name: customer?.billing?.first_name || '',
                    last_name: customer?.billing?.last_name || '',
                    address_1: customer?.billing?.address_1 || '',
                    address_2: customer?.billing?.address_2 || '',
                    city: customer?.billing?.city || '',
                    state: customer?.billing?.state || '',
                    postcode: customer?.billing?.postcode || '',
                    country: customer?.billing?.country || '',
                    phone: customer?.billing?.phone || '',
                    email: customer?.billing?.email || '',
                });
            } catch (error) {
                console.error("Failed to load customer address", error);
            }
        }
        fetchCustomerAddress();
    }, [token, userData?.id]);

    // Coupons are applied by typing a code, not chosen from a list — see SEC-19.
    const applyCoupon = async () => {
        const code = couponCode.trim();
        if (!code) {
            setCouponError('Enter a coupon code.');
            return;
        }
        setCouponChecking(true);
        setCouponError('');
        try {
            const result = await validateCoupon(code);
            setSelectedCoupon(result);
            toast.success(`Coupon ${result.code} applied`);
        } catch (error) {
            setSelectedCoupon(null);
            setCouponError(error.message);
        } finally {
            setCouponChecking(false);
        }
    };

    const removeCoupon = () => {
        setSelectedCoupon(null);
        setCouponCode('');
        setCouponError('');
    };

    const handleAddressChange = (e) => {
        const { name, value } = e.target;
        setBillingInfo(prev => ({ ...prev, [name]: value }));
    };

    const saveAddressToBackend = async () => {
        setLoading(true);
        try {
            const payload = {
                billing: { ...billingInfo },
                shipping: {
                    ...billingInfo,
                    address_2: billingInfo.address_2 || ''
                }
            };

            await updateWooCustomer(userData.id, token, payload);
            toast.success("Address updated successfully");
            setIsModalOpen(false); // Close modal on success
        } catch (error) {
            toast.error("Failed to save address");
        } finally {
            setLoading(false);
        }
    };

    async function getCheckoutData(paid) {
        const billing = { ...billingInfo };
        const shipping = { ...billingInfo };
        delete shipping.email;
        delete shipping.phone;

        let orderData = {
            payment_method: paymentMethod === 'razorpay' ? 'razorpay' : 'cod',
            payment_method_title: paymentMethod === 'razorpay' ? 'Razorpay' : 'Cash on Delivery',
            set_paid: paid,
            status: paid ? 'processing' : 'pending',
            billing,
            shipping,

            line_items: cartItems.map(item => ({
                product_id: item.id,
                quantity: item.quantity || 1,
                ...(item.variation_id && { variation_id: item.variation_id })
            })),

            shipping_lines: [{
                method_id: 'flat_rate',
                method_title: 'Flat Rate',
                total: shippingCharge.toFixed(2),
            }],

            meta_data: [
                { key: "customer_note", value: customerNote || "" },
                { key: "gst_amount", value: gst.toFixed(2) },
                { key: "cgst", value: gstBreakup.cgst.toFixed(2) },
                { key: "sgst", value: gstBreakup.sgst.toFixed(2) },
                { key: "pincode", value: billing.postcode }
            ]

        };

        if (couponCode) {
            orderData.coupon_lines = [{ code: couponCode }];
        }

        if (userData?.id) {
            orderData.customer_id = userData.id;
        }

        return orderData;
    }

    async function createOrderHandler(isPaid, paymentRef = null) {
        try {
            const checkoutData = await getCheckoutData(isPaid);
            await createAnOrder(checkoutData);
            toast.success('Order has been placed successfully!');
            clearCartItems && clearCartItems();
            localStorage.removeItem('cart');
            navigate('/');
        } catch (error) {
            const message = error.response?.data?.message || "Error placing order";
            if (paymentRef) {
                // The money has already moved but no order exists. Without the
                // payment reference there is nothing to reconcile against, so put
                // it in front of the customer rather than only in the console.
                console.error('Payment succeeded but order creation failed.', { paymentRef, error });
                toast.error(
                    `${message}. Your payment reference is ${paymentRef} — ` +
                    `please quote it when contacting support.`,
                    { autoClose: false }
                );
            } else {
                toast.error(message);
            }
        }
    }

    const initiateRazorpayPayment = async () => {
        // Guard against a second checkout being opened. Each Razorpay instance
        // carries its own handler, so two open modals meant two order-creation
        // calls — and each instance initialises and reports independently.
        if (paymentInProgress) return;

        if (!process.env.REACT_APP_RAZORPAY_KEY) {
            toast.error('Online payment is not configured. Please choose Cash on Delivery.');
            return;
        }

        setPaymentInProgress(true);

        try {
            const Razorpay = await loadRazorpayCheckout();

            const rzp = new Razorpay({
                key: process.env.REACT_APP_RAZORPAY_KEY,
                amount: Math.round(Number(finalTotal) * 100),
                currency: 'INR',
                name: 'Pharmacy Store',
                handler: (response) => {
                    setPaymentInProgress(false);
                    // NOTE: the order is still marked paid on the client's word.
                    // Verifying `response.razorpay_signature` server-side is PAY-1
                    // and is not addressed here; the reference is captured so a
                    // failed order can at least be reconciled.
                    createOrderHandler(true, response?.razorpay_payment_id || null);
                },
                prefill: {
                    name: `${billingInfo.first_name} ${billingInfo.last_name}`,
                    email: billingInfo.email,
                    contact: billingInfo.phone,
                },
                // Without this, closing the modal left the flow stuck: the button
                // stayed disabled and the customer could not retry.
                modal: {
                    ondismiss: () => setPaymentInProgress(false),
                },
                theme: { color: '#10B981' },
            });

            rzp.on('payment.failed', (response) => {
                setPaymentInProgress(false);
                toast.error(
                    response?.error?.description || 'Payment failed. No order has been placed.'
                );
            });

            rzp.open();
        } catch (error) {
            // Only reachable before the modal opens, so clearing the flag here is
            // safe — once it is open, the handler/ondismiss/failed paths own it.
            setPaymentInProgress(false);
            toast.error(error.message);
        }
    };

    function handlePlaceOrder(e) {
        e.preventDefault();
        if (paymentInProgress) return;
        if (cartItems.length === 0) return toast.error("Cart is empty");
        if (!billingInfo.address_1 || !billingInfo.postcode) {
            toast.error("Please complete your shipping address");
            setIsModalOpen(true);
            return;
        }
        paymentMethod === 'razorpay' ? initiateRazorpayPayment() : createOrderHandler(false);
    }

    const subTotal = cartItems.reduce((sum, item) => sum + (parseFloat(item.price || 0) * (item.quantity || 1)), 0).toFixed(2);

    // The previous dropdown only ever offered percent coupons, so this divided by 100
    // unconditionally. validateCoupon() accepts any type, so honour discount_type —
    // otherwise a ₹50 fixed-cart coupon would be applied as 50% off.
    const discountAmount = selectedCoupon
        ? (selectedCoupon.discount_type === 'percent'
            ? (Number(subTotal) * Number(selectedCoupon.amount)) / 100
            : Math.min(Number(selectedCoupon.amount), Number(subTotal)))
        : 0;

    const finalTotal = (
        Number(subTotal) - discountAmount + shippingCharge + gst
    ).toFixed(2);

    return (
        <div className="page-wrapper">
            <main className="main-content">
                <div className="container">
                    <h1 className="page-title">Order Summary</h1>
                    <div className="checkout-card">
                        <div className="card-body">
                            <h2 className="section-header">Items in your order</h2>
                            <div className="items-grid">
                                {cartItems.map((item, index) => (
                                    <div key={`${item.id}-${index}`} className="order-item">
                                        <div className="item-thumbnail">
                                            <img src={item.images?.[0]?.src || "https://via.placeholder.com/150"} alt={item.name} />
                                        </div>
                                        <div className="item-info">
                                            <div className="item-top-row">
                                                <h3 className="item-title">{item.name}</h3>
                                                <span className="item-price">₹{(item.price * (item.quantity || 1)).toFixed(2)}</span>
                                            </div>
                                            <div className="item-actions">
                                                <span className="qty-badge">Qty {item.quantity || 1}</span>
                                                <button className="remove-link" onClick={() => onRemoveProduct(item)}>Remove</button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <h2 className="section-header" style={{ marginTop: '20px' }}>Order Details</h2>
                            <div className="info-grid">
                                <div className="info-box">
                                    <h3>Shipping Information</h3>
                                    <div className="info-content">
                                        <div className="address-display-box">
                                            <p><strong>{billingInfo.first_name} {billingInfo.last_name}</strong></p>
                                            <p>{billingInfo.address_1}</p>
                                            {billingInfo.address_2 && <p>{billingInfo.address_2}</p>}
                                            <p>{billingInfo.city}, {billingInfo.state} {billingInfo.postcode}</p>
                                            <p>{billingInfo.country}</p>
                                            <p>{billingInfo.phone}</p>
                                        </div>
                                        <button className="edit-btn" onClick={() => setIsModalOpen(true)}>Change address</button>
                                    </div>
                                </div>

                                <div className="info-box">
                                    <h3>Payment Method</h3>
                                    <div className="info-content">
                                        <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="form-input">
                                            <option value="cod">Cash on Delivery</option>
                                            <option value="razorpay">Razorpay</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="info-box" style={{ gridColumn: '1 / -1' }}>
                                    <h3>Apply Coupon</h3>
                                    <div className="info-content">
                                        {selectedCoupon ? (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <span>
                                                    <strong>{selectedCoupon.code}</strong> applied
                                                    {selectedCoupon.discount_type === 'percent'
                                                        ? ` — ${selectedCoupon.amount}% off`
                                                        : ` — ₹${selectedCoupon.amount} off`}
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={removeCoupon}
                                                    className="form-input"
                                                    style={{ width: 'auto', cursor: 'pointer' }}
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <div style={{ display: 'flex', gap: '8px' }}>
                                                    <input
                                                        type="text"
                                                        value={couponCode}
                                                        onChange={(e) => {
                                                            setCouponCode(e.target.value);
                                                            if (couponError) setCouponError('');
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                applyCoupon();
                                                            }
                                                        }}
                                                        placeholder="Enter coupon code"
                                                        className="form-input"
                                                        disabled={couponChecking}
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={applyCoupon}
                                                        className="form-input"
                                                        style={{ width: 'auto', cursor: 'pointer' }}
                                                        disabled={couponChecking}
                                                    >
                                                        {couponChecking ? 'Checking…' : 'Apply'}
                                                    </button>
                                                </div>
                                                {couponError && (
                                                    <p style={{ color: '#b3261e', margin: '6px 0 0', fontSize: '13px' }}>
                                                        {couponError}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card-footer">
                            <div className="summary-line">
                                <span>Subtotal</span>
                                <span>₹{subTotal}</span>
                            </div>
                            {selectedCoupon && (
                                <div className="summary-line discount">
                                    <span>
                                        Discount ({selectedCoupon.discount_type === 'percent'
                                            ? `${selectedCoupon.amount}%`
                                            : selectedCoupon.code})
                                    </span>
                                    <span>-₹{discountAmount.toFixed(2)}</span>
                                </div>
                            )}
                            <div className="summary-line">
                                <span>Shipping</span>
                                <span>₹{shippingCharge.toFixed(2)}</span>
                            </div>
                            <div className="summary-line">
                                <span>GST</span>
                                <span>₹{gst.toFixed(2)}</span>
                            </div>
                            {(gstBreakup.cgst || gstBreakup.sgst) && (
                                <small style={{ color: "#6b7280" }}>
                                    (CGST ₹{gstBreakup.cgst.toFixed(2)} + SGST ₹{gstBreakup.sgst.toFixed(2)})
                                </small>
                            )}
                            <div className="summary-line total">
                                <span>Total</span>
                                <span>₹{finalTotal}</span>
                            </div>
                            <button
                                className="checkout-btn"
                                onClick={handlePlaceOrder}
                                disabled={paymentInProgress}
                            >
                                <span>
                                    {paymentInProgress
                                        ? 'Opening payment…'
                                        : paymentMethod === 'cod' ? 'Place Order' : 'Proceed to Payment'}
                                </span>
                                <IoMdArrowRoundForward />
                            </button>
                        </div>
                    </div>
                </div>
            </main>

            {/* ADDRESS MODAL */}
            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content card">
                        <div className="card-header">
                            <h3 className="card-title">Update Shipping Address</h3>
                            <button className="icon-btn" onClick={() => setIsModalOpen(false)}><FaTimes /></button>
                        </div>
                        <div className="modal-body">
                            <div className="form-row">
                                <div className="form-group"><label>First Name</label><input name="first_name" className="form-input" value={billingInfo.first_name} onChange={handleAddressChange} /></div>
                                <div className="form-group"><label>Last Name</label><input name="last_name" className="form-input" value={billingInfo.last_name} onChange={handleAddressChange} /></div>
                            </div>
                            <div className="form-group"><label>Address Line 1</label><input name="address_1" className="form-input" value={billingInfo.address_1} onChange={handleAddressChange} /></div>
                            <div className="form-group"><label>Address Line 2 (Optional)</label><input name="address_2" className="form-input" value={billingInfo.address_2} onChange={handleAddressChange} /></div>
                            <div className="form-row">
                                <div className="form-group"><label>City</label><input name="city" className="form-input" value={billingInfo.city} onChange={handleAddressChange} /></div>
                                <div className="form-group"><label>State</label><input name="state" className="form-input" value={billingInfo.state} onChange={handleAddressChange} /></div>
                            </div>
                            <div className="form-row">
                                <div className="form-group"><label>Pincode</label><input name="postcode" className="form-input" value={billingInfo.postcode} onChange={handleAddressChange} /></div>
                                <div className="form-group"><label>Country</label><input name="country" className="form-input" value={billingInfo.country} onChange={handleAddressChange} /></div>
                            </div>
                            <div className="form-group"><label>Phone</label><input name="phone" className="form-input" value={billingInfo.phone} onChange={handleAddressChange} /></div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
                            <button className="btn-primary" onClick={saveAddressToBackend} disabled={loading}>
                                {loading ? 'Saving...' : 'Save Address'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Checkout;